import {
  BufferGeometry,
  Camera,
  CylinderGeometry,
  Material,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { Quick3DPerspectiveCamera } from "../camera/quick3d-perspective-camera";
import * as Config from "../config/ViewerConfig";
import { AutoAttenuableState } from "../measurement/types";

export class AutoAttenuatableArrow extends Mesh {
  private pixelSize: number;
  private _offset: Vector3;
  private _position: Vector3;
  private _defaultGeometry: BufferGeometry;
  private _selectedGeometry: BufferGeometry;
  private _hoveredGeometry: BufferGeometry;
  private _defaultLineMaterial: Material;
  private _selectedLineMaterial: Material;
  private _hoveredLineMaterial: Material;

  constructor(
    material: Material = new MeshBasicMaterial({
      color: 0x0000ff,
      depthTest: false,
    }),
    pixelSize: number
  ) {
    const geometry = new CylinderGeometry(
      Config.DEFAULT_ARROW_TOP_RADIUS,
      Config.DEFAULT_ARROW_BOTTOM_RADIUS,
      2,
      8,
      1,
      false
    );
    geometry.rotateX(Math.PI / 2);
    super(geometry, material);

    const selectedGeometry = new CylinderGeometry(
      Config.SELECTED_ARROW_TOP_RADIUS,
      Config.SELECTED_ARROW_BOTTOM_RADIUS,
      2,
      8,
      1,
      false
    );
    selectedGeometry.rotateX(Math.PI / 2);

    const hoveredGeometry = new CylinderGeometry(
      Config.HOVERED_ARROW_TOP_RADIUS,
      Config.HOVERED_ARROW_BOTTOM_RADIUS,
      2,
      8,
      1,
      false
    );

    hoveredGeometry.rotateX(Math.PI / 2);
    this._hoveredGeometry = hoveredGeometry;
    this._hoveredLineMaterial = new MeshBasicMaterial({
      color: Config.HOVERED_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });

    this._selectedGeometry = selectedGeometry;
    this._defaultGeometry = geometry;
    this._defaultLineMaterial = material;
    this._selectedLineMaterial = new MeshBasicMaterial({
      color: Config.SELECTED_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this.pixelSize = pixelSize;
    this._offset = new Vector3();
    this._position = new Vector3();
    this.onBeforeRender = function (
      _ifcRenderer: WebGLRenderer,
      _nonClippingScene: Scene,
      camera: Camera
    ): void {
      let scale = 1;
      const windowScale = Math.sqrt(
        (1280 * 720) / (window.screen.width * window.screen.height)
      );
      if (camera instanceof OrthographicCamera) {
        scale =
          ((camera.right - camera.left) / camera.zoom) * 0.01 * windowScale;
      }
      if (camera instanceof Quick3DPerspectiveCamera) {
        scale = this.position.distanceTo(camera.position) * 0.02 * windowScale;
      }

      const scaledOffset = new Vector3()
        .copy(this._offset)
        .multiplyScalar(scale);
      this.position.copy(
        new Vector3().subVectors(this._position, scaledOffset)
      );
      this.scale.set(scale, scale, scale);
      this.updateMatrixWorld();
    };
  }

  public dispose(): void {
    if (this.parent) {
      this.parent.remove(this);
    }

    this.geometry?.dispose();

    if (Array.isArray(this.material)) {
      for (const m of this.material) {
        m.dispose();
      }
    } else {
      this.material?.dispose();
    }
    this.geometry = undefined as unknown as BufferGeometry;
    this.material = undefined as unknown as Material;
  }

  public setOffset(offset: Vector3): void {
    this._offset.copy(offset);
  }

  public setPosition(position: Vector3): void {
    this.position.copy(position);
    this._position.copy(position);
  }

  public setMaterialByState(state: AutoAttenuableState) {
    this.material = {
      default: this._defaultLineMaterial,
      hovered: this._hoveredLineMaterial,
      selected: this._selectedLineMaterial,
    }[state];
  }

  public setGeometryByState(state: AutoAttenuableState) {
    this.geometry = {
      default: this._defaultGeometry,
      hovered: this._hoveredGeometry,
      selected: this._selectedGeometry,
    }[state];
  }

  public changeRenderOrderByState(state: AutoAttenuableState) {
    this.renderOrder = {
      default: 1,
      hovered: 1,
      selected: 1,
    }[state];
  }
}
